import { GD } from "../GD"
import { clearAuthStorage } from "../utils/authStorage"
import { clearFreshUserBalanceOverrides } from "./freshUserBalanceOverrides"
import { clearCardBlockSessions } from "./cardBlockSessions"
import { getIssuerRequestContext } from "./issuerRequestContext"

const FRESH_PROFILE_PATCH_TTL_MS = 60_000

const profileValuesMatch = (left: unknown, right: unknown) => {
	if (Object.is(left, right)) return true
	if (left === undefined || right === undefined) return false
	return JSON.stringify(left) === JSON.stringify(right)
}

export enum AUTH_STATE {
	AUTHORIZED = "authorized",
	UNAUTHORIZED = "unauthorized",
	SELECT_PROJECT = "select_project"
}

export enum USER_ROLE {
	ADMIN = "admin",
	TEAM_LEAD = "teamlead",
	BUYER = "buyer"
}

export interface ILoginUser {
	uid: string
	status: string
	name: string
	email: string
	created_at: string
	invited_by_uid: string | null
	company_role: string
	company_uid: string
	company_status: string
	team_status: string
	team_balance: number
	team_balances?: IUserIssuerBalance[]
	twofa_method?: TTwoFactorMethod | null
}

export type TCompanyType = "company" | "private"

export interface ILoginCompany {
	uid: string
	name: string
	number: string
	vat_number: string
	created_at: string
	balance: number
	balances?: ICompanyIssuer[]
	type?: TCompanyType
}

export interface ILoginResponse {
	token: string
	user: ILoginUser
	company: ILoginCompany
	// Present when this login must finish our KYC before entering the cabinet.
	onboarding?: IOnboarding
}

export interface ILoginTwoFactorChallenge {
	twofa_required: true
	method: TTwoFactorMethod
	pending_token: string
}

export interface IEnableTwoFactorResponse {
	url: string
	pending_token?: string
}

export interface TUser {
	uid: string
	name: string
	company_role: string
	email: string
	status: string
	created_at: string
	company_uid?: string
	company_status?: string
	invited_by_uid?: string | null
	last_login_at?: string | null
	team_status?: string
	team_balance?: number
	team_balances?: IUserIssuerBalance[]
	team_uid?: string
	team_name?: string
	twofa_method?: TTwoFactorMethod | null
	cards_balance?: number
	cards_count?: number
	card_issue_limit?: number
	card_issue_limit_status?: "active" | "disabled"
}

type TPendingTwoFactorLogin = {
	login: string
	password: string
	remember: boolean
	pendingToken?: string
}

export class AuthService {
	authState = AUTH_STATE.UNAUTHORIZED
	profile: TUser | null = null
	company: ILoginCompany | null = null
	onboarding: IOnboarding | null = null
	key: string = ""
	pendingTwoFactorLogin: TPendingTwoFactorLogin | null = null
	pendingTwoFactorSetupToken: string | null = null
	private profileRefreshSequence = 0
	private companyRefreshSequence = 0
	private freshProfilePatches = new Map<
		string,
		{ patch: Partial<TUser>; expiresAt: number }
	>()

	constructor() {
		GD.REQ_AUTH_LOGIN.listener = async (req) => this.handleAuthLogin(req)
		GD.REQ_AUTH_2FA_ENABLE.listener = async (req) => this.enableTwoFactor(req)
		GD.REQ_AUTH_2FA_DISABLE.listener = async (req) => this.disableTwoFactor(req)
		GD.REQ_AUTH_2FA_VALIDATE.listener = async (req) => this.validateTwoFactor(req)
		GD.S_AUTH_STATUS.onSubscribe = (cb) => cb!(this.authState)

		GD.S_AUTH_LOG_OUT.subscribe(() => {
			clearAuthStorage()
			clearFreshUserBalanceOverrides()
			clearCardBlockSessions()
			this.key = ""
			this.profile = null
			this.freshProfilePatches.clear()
			this.company = null
			this.onboarding = null
			this.pendingTwoFactorLogin = null
			this.pendingTwoFactorSetupToken = null
			this.authState = AUTH_STATE.UNAUTHORIZED
			GD.S_COMPANY_UPDATED.invoke(null)
			GD.S_AUTH_STATUS.invoke(this.authState)
			window.location.href = "/"
		})

		GD.S_PROJECT_SELECTED.subscribe((p) => {
			if (p !== null && this.authState !== AUTH_STATE.AUTHORIZED) {
				this.authState = AUTH_STATE.AUTHORIZED
				GD.S_AUTH_STATUS.invoke(this.authState)
			}
		})

		GD.REQ_PROFILE.listener = async () => this.refreshCurrentProfile()
		GD.S_PROFILE_UPDATED.subscribe(patch => this.patchCurrentProfile(patch))

		GD.REQ_COMPANY.listener = async () => this.company
		GD.REQ_COMPANY_REFRESH.listener = async () => this.refreshCurrentCompany()
		GD.S_COMPANY_UPDATED.onSubscribe = callback => callback?.(this.company)

		GD.REQ_AUTH_TOKEN.listener = async () => this.key

		GD.S_SELECT_PROJECT.subscribe(() => {
			this.authState = AUTH_STATE.SELECT_PROJECT
			GD.S_AUTH_STATUS.invoke(this.authState)
		})

		GD.REQ_AUTH_SIGNUP.listener = async (data) => this.handleAuthSignup(data)

		GD.REQ_AUTH_REGISTER.listener = async (data) => this.register(data)
		GD.REQ_AUTH_REGISTER_CONFIRM.listener = async (data) => this.confirmRegistration(data)
		GD.REQ_AUTH_REGISTER_RESEND.listener = async (data) => this.resendRegistration(data)
		GD.REQ_REFERRAL_CHECK.listener = async (data) => this.checkReferral(data)

		GD.REQ_ONBOARDING.listener = async () => this.onboarding
		GD.REQ_ONBOARDING_SET_STATUS.listener = async (status) =>
			this.setOnboardingKycStatus(status)

		const remember = localStorage.getItem("auth")

		if (remember) {
			try {
				const response = JSON.parse(remember)

				if (response && response.data && response.data.token) {
					//todo - validate with TUSER

					this.authorize(response.data)
				}
			} catch (e) {
				console.error("cant parse stored auth data")
			}
		}
	}

	async handleAuthLogin(
		form: IAuthForm
	): Promise<IError | ILoginTwoFactorChallenge | null> {
		if (!form) return null

		if (form.login.length < 3 || form.password.length < 4)
			return { code: 1, message: "Check login and password" }

		this.pendingTwoFactorLogin = null
		this.pendingTwoFactorSetupToken = null

		const payload: any = {
			login: form.login,
			password: form.password
		}
		if (form.mask_email) payload.mask_email = form.mask_email

		const response = (await GD.REQ_API.request({
			method: "users/login",
			data: payload
		})) as unknown as {
			data: (ILoginResponse & { IsMasking?: boolean }) | ILoginTwoFactorChallenge
			error: IError | null
		}

		if (response.error) {
			if (response.error.code === 2802) {
				return {
					...response.error,
					message: "Account is blocked. Contact your admin."
				}
			}
			if (response.error.code === 4001) {
				this.pendingTwoFactorLogin = {
					login: form.login,
					password: form.password,
					remember: !!form.controls?.remember
				}
			}
			return response.error
		}

		if (
			response.data &&
			"twofa_required" in response.data &&
			response.data.twofa_required
		) {
			this.pendingTwoFactorLogin = {
				login: form.login,
				password: form.password,
				remember: !!form.controls?.remember,
				pendingToken: response.data.pending_token
			}

			return response.data
		}

		const loginResponse = response.data as ILoginResponse & { IsMasking?: boolean }

		if (loginResponse && loginResponse.IsMasking) {
			return { code: 999, message: "IsMasking" }
		}

		if (form.controls.remember) {
			localStorage.setItem("auth", JSON.stringify(response))
		}

		this.authorize(loginResponse)

		return null
	}

	private readStoredJson<T>(key: string): T | null {
		const raw = localStorage.getItem(key)
		if (!raw) return null

		try {
			return JSON.parse(raw) as T
		} catch {
			return null
		}
	}

	private syncStoredAuth() {
		if (this.profile) {
			localStorage.setItem("user", JSON.stringify(this.profile))
		}

		if (this.company) {
			localStorage.setItem("company", JSON.stringify(this.company))
		}

		const storedAuth = this.readStoredJson<{ data?: ILoginResponse }>("auth")
		if (!storedAuth?.data) return

		storedAuth.data = {
			...storedAuth.data,
			user: (this.profile as ILoginUser) ?? storedAuth.data.user,
			company: this.company ?? storedAuth.data.company
		}

		localStorage.setItem("auth", JSON.stringify(storedAuth))
	}

	private setCurrentCompany(company: ILoginCompany | null) {
		this.company = company
		this.syncStoredAuth()
		GD.S_COMPANY_UPDATED.invoke(company)
	}

	private refreshCurrentProfile = async (): Promise<TUser | null> => {
		if (!this.profile?.uid || !this.key) return this.profile

		const requestIssuer = getIssuerRequestContext().issuer
		const refreshSequence = ++this.profileRefreshSequence
		const currentProfile = this.profile
		const currentToken = this.key
		let response: IAPIResponse<Partial<TUser>>
		try {
			response = (await GD.REQ_API.request({
				httpMethod: "POST",
				method: "users/get/me",
				data: {}
			})) as IAPIResponse<Partial<TUser>>
		} catch {
			return this.profile
		}

		if (
			!response.error &&
			response.data &&
			refreshSequence === this.profileRefreshSequence &&
			this.key === currentToken &&
			this.profile?.uid === currentProfile.uid
		) {
			const serverProfile = {
				...currentProfile,
				...response.data
			}
			const freshPatch = this.getUnreconciledFreshProfilePatch(response.data, requestIssuer)
			this.profile = freshPatch ? { ...serverProfile, ...freshPatch } : serverProfile
			this.syncStoredAuth()
		}

		return this.profile
	}

	private refreshCurrentCompany = async (): Promise<ILoginCompany | null> => {
		if (!this.company?.uid || !this.key) return this.company

		const refreshSequence = ++this.companyRefreshSequence
		const currentCompany = this.company
		const currentToken = this.key
		let response: IAPIResponse<Partial<ILoginCompany>>
		try {
			response = (await GD.REQ_API.request({
				httpMethod: "GET",
				method: "companies/get/me"
			})) as IAPIResponse<Partial<ILoginCompany>>
		} catch {
			return this.company
		}

		if (
			!response.error &&
			response.data &&
			refreshSequence === this.companyRefreshSequence &&
			this.key === currentToken &&
			this.company?.uid === currentCompany.uid
		) {
			this.setCurrentCompany({
				...currentCompany,
				...response.data
			})
		}

		return this.company
	}

	private patchCurrentProfile(patch: Partial<TUser>) {
		if (!this.profile) return
		const patchKey = this.getProfilePatchKey(getIssuerRequestContext().issuer)
		const currentPatch = this.freshProfilePatches.get(patchKey)
		const existingPatch =
			currentPatch && currentPatch.expiresAt > Date.now()
				? currentPatch.patch
				: {}
		this.freshProfilePatches.set(patchKey, {
			patch: { ...existingPatch, ...patch },
			expiresAt: Date.now() + FRESH_PROFILE_PATCH_TTL_MS
		})

		this.profile = {
			...this.profile,
			...patch
		}

		this.syncStoredAuth()
		GD.REQ_PROFILE.request()
	}

	private getProfilePatchKey(issuer: number | null) {
		return String(issuer ?? "default")
	}

	private getUnreconciledFreshProfilePatch(
		serverPatch: Partial<TUser>,
		issuer: number | null
	) {
		const patchKey = this.getProfilePatchKey(issuer)
		const freshProfilePatch = this.freshProfilePatches.get(patchKey)
		if (!freshProfilePatch) return null
		if (freshProfilePatch.expiresAt <= Date.now()) {
			this.freshProfilePatches.delete(patchKey)
			return null
		}

		const pendingEntries = Object.entries(freshProfilePatch.patch).filter(
			([key, value]) => !profileValuesMatch(serverPatch[key as keyof TUser], value)
		)
		if (!pendingEntries.length) {
			this.freshProfilePatches.delete(patchKey)
			return null
		}

		const pendingPatch = Object.fromEntries(pendingEntries) as Partial<TUser>
		this.freshProfilePatches.set(patchKey, {
			...freshProfilePatch,
			patch: pendingPatch
		})
		return pendingPatch
	}

	private normalizeLoginResponse(payload: unknown): ILoginResponse | null {
		if (!payload || typeof payload !== "object") {
			return null
		}

		const data = payload as Partial<ILoginResponse> & { token?: unknown }
		if (typeof data.token !== "string" || !data.token) {
			return null
		}

		const user =
			(data.user as ILoginUser | undefined) ??
			(this.readStoredJson<TUser>("user") as ILoginUser | null) ??
			null
		const company =
			(data.company as ILoginCompany | undefined) ??
			this.readStoredJson<ILoginCompany>("company")

		if (!user || !company) {
			return null
		}

		return {
			token: data.token,
			user,
			company
		}
	}

	async enableTwoFactor(req: {
		method: TTwoFactorMethod
	}): Promise<IAPIResponse<IEnableTwoFactorResponse>> {
		const response = (await GD.REQ_API.request({
			method: "users/2fa/enable",
			httpMethod: "POST",
			data: req
		})) as IAPIResponse<IEnableTwoFactorResponse | string>

		if (response.error) {
			return response as IAPIResponse<IEnableTwoFactorResponse>
		}

		if (typeof response.data === "string") {
			this.pendingTwoFactorSetupToken = null
			return { data: { url: response.data } }
		}

		this.pendingTwoFactorSetupToken = response.data?.pending_token ?? null

		return response as IAPIResponse<IEnableTwoFactorResponse>
	}

	async disableTwoFactor(req: { password?: string }): Promise<IError | null> {
		const response = (await GD.REQ_API.request({
			method: "users/2fa/disable",
			httpMethod: "POST",
			data: req?.password ? { password: req.password } : {}
		})) as IAPIResponse<boolean>

		if (response.error) {
			return response.error
		}

		if (response.data !== true) {
			return { code: -1, message: "Failed to disable 2FA" }
		}

		this.pendingTwoFactorSetupToken = null
		this.patchCurrentProfile({ twofa_method: null })
		return null
	}

	async validateTwoFactor(req: {
		totp: string
		method: TTwoFactorMethod
	}): Promise<IError | null> {
		const totp = req?.totp?.trim()
		if (!totp) {
			return { code: 1, message: "Verification code is required" }
		}

		const payload: Record<string, unknown> = { totp, method: req?.method ?? "totp" }
		if (this.pendingTwoFactorLogin?.pendingToken) {
			payload.pending_token = this.pendingTwoFactorLogin.pendingToken
		} else if (this.pendingTwoFactorSetupToken) {
			payload.pending_token = this.pendingTwoFactorSetupToken
		} else if (this.pendingTwoFactorLogin) {
			payload.login = this.pendingTwoFactorLogin.login
			payload.password = this.pendingTwoFactorLogin.password
		}

		const response = (await GD.REQ_API.request({
			method: "users/2fa/validate",
			httpMethod: "POST",
			data: payload
		})) as IAPIResponse<ILoginResponse | { token: string } | IStatusResponse | boolean>

		if (response.error) {
			return response.error
		}

		if (this.pendingTwoFactorLogin) {
			const remember = this.pendingTwoFactorLogin.remember
			const normalizedResponse = this.normalizeLoginResponse(response.data)

			this.pendingTwoFactorLogin = null

			if (!normalizedResponse) {
				return { code: -1, message: "2FA validated, but login response is incomplete" }
			}

			if (remember) {
				localStorage.setItem("auth", JSON.stringify({ data: normalizedResponse }))
			}

			this.authorize(normalizedResponse)
			return null
		}

		this.pendingTwoFactorSetupToken = null
		this.patchCurrentProfile({ twofa_method: "totp" })
		return null
	}

	async handleAuthSignup(data: ICorporateSignupBody): Promise<IAPIResponse> {
		const response = (await GD.REQ_API.request({
			method: "accounts/signup",
			data: {
				name: data.name,
				email: data.email,
				password: data.pass,
				company_name: data.company_name,
				country_code: data.country_code,
				registration_number: data.registration_number
			}
		})) as unknown as { data: ILoginResponse; error: IError | null }

		if (response.error) return response as unknown as IAPIResponse

		this.authorize(response.data)

		return response.data as unknown as IAPIResponse
	}

	/**
	 * Public self-registration. Creates a private or corporate company and
	 * sends a confirmation email. Unlike the legacy accounts/signup path this does
	 * NOT authorize the user — they must confirm their email first, then log in.
	 */
	async register(data: IRegisterBody): Promise<IAPIResponse<IStatusResponse>> {
		return (await GD.REQ_API.request({
			method: "users/register",
			httpMethod: "POST",
			data: {
				name: data.name,
				email: data.email,
				password: data.password,
				...(data.type ? { type: data.type } : {}),
				...(data.company_name ? { company_name: data.company_name } : {}),
				...(data.registration_number ? { registration_number: data.registration_number } : {}),
				...(data.referral_code ? { referral_code: data.referral_code } : {})
			}
		})) as IAPIResponse<IStatusResponse>
	}

	async checkReferral(
		data: IReferralCheckBody
	): Promise<IAPIResponse<IReferralCheckResponse>> {
		return (await GD.REQ_API.request({
			method: "companies/referral/check",
			httpMethod: "POST",
			data: { code: data.code }
		})) as IAPIResponse<IReferralCheckResponse>
	}

	/** Confirm the email address with the one-time token from the letter. */
	async confirmRegistration(
		data: IRegisterConfirmBody
	): Promise<IAPIResponse<IStatusResponse>> {
		return (await GD.REQ_API.request({
			method: "users/register/confirm",
			httpMethod: "POST",
			data: { token: data.token }
		})) as IAPIResponse<IStatusResponse>
	}

	/**
	 * Resend the confirmation letter. The backend always answers { status: true }
	 * (it never reveals whether the account exists) and throttles by email.
	 */
	async resendRegistration(
		data: IRegisterResendBody
	): Promise<IAPIResponse<IStatusResponse>> {
		return (await GD.REQ_API.request({
			method: "users/register/resend",
			httpMethod: "POST",
			data: { email: data.email }
		})) as IAPIResponse<IStatusResponse>
	}

	/**
	 * Lift (or update) the onboarding gate once KYC resolves. Called by the
	 * onboarding view when our_kyc_status reports "approved" so the private UI
	 * can unlock without forcing a re-login. Provider KYC never gates entry.
	 */
	setOnboardingKycStatus(status: TKycStatus) {
		if (!this.onboarding) {
			this.onboarding = { required: status !== "approved", kyc_status: status }
		} else {
			this.onboarding = {
				...this.onboarding,
				kyc_status: status,
				required: status === "approved" ? false : this.onboarding.required
			}
		}

		const storedAuth = this.readStoredJson<{ data?: ILoginResponse }>("auth")
		if (storedAuth?.data) {
			storedAuth.data = { ...storedAuth.data, onboarding: this.onboarding }
			localStorage.setItem("auth", JSON.stringify(storedAuth))
		}

		GD.S_ONBOARDING_UPDATED.invoke(this.onboarding)
		GD.REQ_ONBOARDING.request()
	}

	async authorize(
		response:
			| ILoginResponse
			| {
					token: string
					user?: Partial<TUser>
					company?: { uid: string; name: string }
					onboarding?: IOnboarding
			  }
	) {
		clearFreshUserBalanceOverrides()
		this.freshProfilePatches.clear()
		this.key = response.token
		this.profile = (response.user as TUser) || null
		this.company = (response.company as ILoginCompany) || null
		this.onboarding = (response as ILoginResponse).onboarding ?? null
		GD.S_ONBOARDING_UPDATED.invoke(this.onboarding)
		GD.S_COMPANY_UPDATED.invoke(this.company)

		if (response.company) {
			localStorage.setItem("company", JSON.stringify(response.company))
		}

		if (response.user) {
			localStorage.setItem("user", JSON.stringify(response.user))
		}

		GD.REQ_COMPANY.request()

		// todo handle if exists within multiple companies
		GD.S_PROJECT_SELECTED.invoke({
			id: 0,
			name: this.company?.name || "Main",
			roles: []
		})
	}
}
